import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { HOMESTAY } from "@/lib/data";

const SECTIONS = [
  {
    title: "Information we collect",
    body: (
      <>
        <p>
          When you submit an inquiry on this website or contact us via WhatsApp,
          email or phone, we may collect the following information:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Your name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Number of guests and your preferred dates</li>
          <li>Any message or special request you choose to share with us</li>
          <li>Basic technical data such as your IP address, browser type, and pages visited (via standard server logs)</li>
        </ul>
      </>
    ),
  },
  {
    title: "How we use your information",
    body: (
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To respond to your inquiry and confirm availability</li>
        <li>To coordinate your stay before, during and after your booking</li>
        <li>To send occasional updates relevant to your reservation</li>
        <li>To improve our website and the guest experience</li>
        <li>To comply with applicable legal or accounting obligations</li>
      </ul>
    ),
  },
  {
    title: "Sharing your information",
    body: (
      <p>
        We do not sell or rent your personal information. We only share what is
        strictly necessary with trusted partners that help us operate — for
        example, our email service provider (Gmail) for sending you booking
        communication, and our hosting infrastructure. We never share your
        contact details with marketers or unrelated third parties.
      </p>
    ),
  },
  {
    title: "Cookies & analytics",
    body: (
      <p>
        Our site may use minimal cookies or browser storage to remember basic
        preferences. We may also use privacy-friendly analytics to understand
        broad patterns (such as page views) — these tools do not personally
        identify you.
      </p>
    ),
  },
  {
    title: "Data security",
    body: (
      <p>
        All inquiries are sent over HTTPS and stored on secure servers. We
        restrict access to your data to the people responsible for managing
        your booking. While no online service can be 100% secure, we take
        reasonable steps to protect the information you share.
      </p>
    ),
  },
  {
    title: "Data retention",
    body: (
      <p>
        We retain inquiry and booking information only as long as needed to
        serve you and to comply with legal or accounting requirements. You may
        request deletion of your records at any time.
      </p>
    ),
  },
  {
    title: "Your rights",
    body: (
      <p>
        You have the right to access, correct or delete the personal
        information we hold about you. To exercise any of these rights, write
        to us at the email below and we will respond within a reasonable
        timeframe.
      </p>
    ),
  },
  {
    title: "Children's privacy",
    body: (
      <p>
        Our website is not directed at children under 13, and we do not
        knowingly collect information from them. If you believe a minor has
        provided us with personal data, please reach out and we will delete it.
      </p>
    ),
  },
  {
    title: "Changes to this policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. Any changes will
        be posted on this page with a revised "last updated" date.
      </p>
    ),
  },
];

export default function PrivacyPage() {
  React.useEffect(() => {
    document.title = "Privacy Policy | Mrinmaya vacation home";
  }, []);

  return (
    <LegalLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      testId="privacy-page"
    >
      <p className="text-xs uppercase tracking-[0.25em] text-mrn-terracotta font-medium">
        Last updated · {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
      </p>
      <p>
        Your privacy matters to us. This page explains what information we
        collect when you visit our website or get in touch about a stay at
        Mrinmaya vacation home, how we use it, and the choices you have.
      </p>

      <div className="mt-8 space-y-10">
        {SECTIONS.map((s, idx) => (
          <section key={s.title} data-testid={`privacy-section-${idx}`}>
            <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
              {s.title}
            </h2>
            <div className="mt-3">{s.body}</div>
          </section>
        ))}

        <section>
          <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
            Contact us
          </h2>
          <p className="mt-3">
            Questions about this Privacy Policy or your data? Reach us at{" "}
            <a
              href={`mailto:${HOMESTAY.contact.email}`}
              className="text-mrn-terracotta link-underline"
            >
              {HOMESTAY.contact.email}
            </a>{" "}
            or{" "}
            <a
              href={`tel:${HOMESTAY.contact.phone}`}
              className="text-mrn-terracotta link-underline"
            >
              {HOMESTAY.contact.phoneDisplay}
            </a>
            .
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}
